import omit from 'lodash/omit';

import { BaseApiError, isCancel } from '@lumapps/base-api';
import { isUserDirectory } from '@lumapps/content-types/utils';
import { getContentForView } from '@lumapps/contents/api/contents';
import { contentSelector } from '@lumapps/contents/ducks/selectors';
import { actions as contentActions } from '@lumapps/contents/ducks/slice';
import { ContentState } from '@lumapps/contents/ducks/types';
import { Content, type ReadModeContent } from '@lumapps/contents/types';
import { error } from '@lumapps/notifications';
import { Dispatch, GetBaseState } from '@lumapps/redux/types';
import { notFoundRoute } from '@lumapps/router';
import { RouterApi } from '@lumapps/router/api';
import { GLOBAL } from '@lumapps/translations';
import { fetchLayout } from '@lumapps/widget-layout/ducks/thunks';
import { Layout, LayoutOptions } from '@lumapps/widget-layout/types';

const getContentPromise = (
    contentId: string,
    currentContent: ContentState,
    skipContentHeader?: boolean,
): Promise<ReadModeContent> => {
    if (currentContent.id === contentId) {
        // Use content from store.
        return Promise.resolve(omit(currentContent, 'entities') as ReadModeContent);
    }

    const contentFromStore = currentContent.entities?.[contentId];
    if (contentFromStore) {
        // Use content from store.
        return Promise.resolve(contentFromStore);
    }

    // Fetch content.
    return getContentForView(contentId, { skipHeader: skipContentHeader });
};

/**
 * Fetch layout for content
 */
export const fetchContentLayout =
    (options: {
        contentId: string;
        fetch: (contentId: string, languages: string) => Promise<Layout>;
        enabledWidgetTypes?: LayoutOptions['enabledWidgetTypes'];
        redirect?: RouterApi['redirect'];
        skipContentHeader?: boolean;
    }) =>
    async (dispatch: Dispatch, getState: GetBaseState) => {
        let content: Content | undefined;
        try {
            const { contentId, fetch, enabledWidgetTypes, skipContentHeader } = options;

            const state = getState();

            const currentContent = contentSelector(state);

            // Fetch content (if needed) & layout
            const contentPromise = getContentPromise(contentId, currentContent, skipContentHeader);
            const layoutPromise = dispatch(fetchLayout({ fetchParams: contentId, fetch, enabledWidgetTypes }));

            // Reset & then update content in store.
            content = await contentPromise;
            dispatch(contentActions.resetContent());
            dispatch(contentActions.setContent(content));

            // Fetch layout & update store
            return await layoutPromise;
        } catch (err) {
            if (isCancel(err)) {
                return undefined;
            }

            if (isUserDirectory(content?.type)) {
                // Ignore errors on UD (the content page will redirect to the UD page)
                return undefined;
            }

            if (err instanceof BaseApiError) {
                // Redirect 404/403 to not found page.
                const { redirect } = options;
                if (redirect && (err.response?.status === 404 || err.response?.status === 403)) {
                    redirect(notFoundRoute);
                    return undefined;
                }
            }

            // Display generic error.
            await dispatch(error({ translate: GLOBAL.GENERIC_ERROR }));
            return undefined;
        }
    };
